import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from './AuthContext';

export interface PointsHistoryItem {
  id: string;
  points: number;
  transaction_type: string;
  source_type: string;
  description: string;
  created_at: string;
}

interface PointsContextType {
  userPoints: number;
  pointsHistory: PointsHistoryItem[];
  loadingHistory: boolean;
  historyError: string | null;
  refreshPoints: () => Promise<void>;
  fetchPointsHistory: () => Promise<void>;
}

const PointsContext = createContext<PointsContextType>({
  userPoints: 0,
  pointsHistory: [],
  loadingHistory: false,
  historyError: null,
  refreshPoints: async () => {},
  fetchPointsHistory: async () => {}
});

export const usePoints = () => useContext(PointsContext);

export const PointsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userPoints, setUserPoints] = useState<number>(0);
  const [pointsHistory, setPointsHistory] = useState<PointsHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchUserPointsInProgressRef = useRef(false);
  const fetchPointsHistoryInProgressRef = useRef(false);

  const fetchUserPoints = useCallback(async () => {
    if (!user || fetchUserPointsInProgressRef.current) return;
    
    fetchUserPointsInProgressRef.current = true;
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('users')
        .select('points')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      if (data) {
        setUserPoints(data.points || 0);
      }
    } catch (err) {
      console.error('Error fetching user points:', err);
    } finally {
      fetchUserPointsInProgressRef.current = false;
    }
  }, [user]); // Assuming supabase client is stable via its import and doesn't need to be a dep here unless it changes



  const fetchPointsHistory = useCallback(async () => {
    if (!user || fetchPointsHistoryInProgressRef.current) return;
    
    fetchPointsHistoryInProgressRef.current = true;
    
    try {
      setLoadingHistory(true);
      setHistoryError(null);
      
      // Fetch points history from points_history table exclusively
      console.log('Fetching points history from points_history table');
      const { data: pointsHistoryData, error: pointsHistoryError } = await supabase
        .from('points_history')
        .select('id, user_id, amount, transaction_type, description, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (pointsHistoryError) {
        console.warn('Failed to fetch points history:', pointsHistoryError);
        setPointsHistory([]);
        setHistoryError((pointsHistoryError as Error).message); // Type assertion for error
      } else {
        // Map data to PointsHistoryItem format, adding source_type default based on transaction_type
        const historyItems = pointsHistoryData.map(item => ({
          id: item.id,
          points: item.amount,
          transaction_type: item.transaction_type,
          source_type: item.transaction_type, // Set default source_type to transaction_type for compatibility
          description: item.description,
          created_at: item.created_at
        })) as PointsHistoryItem[]; // Cast to PointsHistoryItem to resolve type error
        setPointsHistory(historyItems);
      }
      setLoadingHistory(false);
    } catch (error) {
      console.error('Error fetching points history:', error);
      setPointsHistory([]);
      setHistoryError((error as Error).message); // Type assertion for catch block error
    } finally {
      fetchPointsHistoryInProgressRef.current = false;
    }
  }, [user, setLoadingHistory, setHistoryError, setPointsHistory]); // Assuming supabase client is stable



  // Refresh points - can be called from anywhere
  const refreshPoints = useCallback(async () => {
    // These calls are now individually re-entrant safe.
    // Consider if they should run in parallel or if one depends on the other.
    // For refreshing, parallel might be fine.
    fetchUserPoints(); 
    fetchPointsHistory();
  }, [fetchUserPoints, fetchPointsHistory]);



  // Initial load and when user changes
  useEffect(() => {
    if (user) {
      fetchUserPoints();
      fetchPointsHistory();
    } else {
      setUserPoints(0);
      setPointsHistory([]);
      // Reset in-progress flags if user logs out or becomes null
      fetchUserPointsInProgressRef.current = false;
      fetchPointsHistoryInProgressRef.current = false;
    }
  }, [user, fetchUserPoints, fetchPointsHistory]);

  // Setup realtime subscriptions for automatic updates
  useEffect(() => {
    if (!user) return;

    console.log('🔔 Setting up realtime subscriptions for user:', user.id);

    // Subscribe to user points changes
    const userPointsChannel = supabase
      .channel('user_points_updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'users',
          filter: `id=eq.${user.id}`
        },
        (payload) => {
          console.log('🔔 User points updated via realtime:', payload);
          if (payload.new && 'points' in payload.new) {
            const newPoints = payload.new.points || 0;
            console.log('🔔 Setting new points value:', newPoints);
            setUserPoints(newPoints);
          }
        }
      )
      .subscribe((status) => {
        console.log('🔔 User points channel subscription status:', status);
      });

    // Subscribe to points history changes
    const pointsHistoryChannel = supabase
      .channel('points_history_updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'points_history',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('🔔 Points history updated via realtime:', payload);
          // Refresh the full history to maintain correct order
          fetchPointsHistory();
          // Also refresh user points since points_history insert means points changed
          console.log('🔔 Fetching updated user points after points_history change...');
          fetchUserPoints();
        }
      )
      .subscribe((status) => {
        console.log('🔔 Points history channel subscription status:', status);
      });

    // Cleanup subscriptions on unmount or user change
    return () => {
      console.log('🔔 Cleaning up realtime subscriptions');
      supabase.removeChannel(userPointsChannel);
      supabase.removeChannel(pointsHistoryChannel);
    };
  }, [user, fetchPointsHistory, fetchUserPoints]);



  return (
    <PointsContext.Provider value={{ 
      userPoints, 
      pointsHistory, 
      loadingHistory, 
      historyError, 
      refreshPoints, 
      fetchPointsHistory 
    }}>
      {children}
    </PointsContext.Provider>
  );
};
